/**
 * Scene Viewer Component
 *
 * Public viewer for individual scenes from the gallery.
 * Displays a single scene using StaticSceneDisplay for instant loading.
 */

import React, { useState, useEffect } from 'react';
import StaticSceneDisplay from './StaticSceneDisplay';
import MainHeader from './MainHeader';
import MaterialIcon from './MaterialIcon';
import parallaxService from '../services/parallaxService';
import navigationService from '../services/navigationService';
import logger from '../utils/logger';
import './SceneViewer.css';

const SceneViewer = () => {
  const [scene, setScene] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sceneStickers, setSceneStickers] = useState([]);

  // Load scene on mount
  useEffect(() => {
    const loadScene = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get scene name from sessionStorage
        const sceneName = sessionStorage.getItem('viewingSceneName');

        if (!sceneName) {
          setError('No scene selected. Please select a scene from the gallery.');
          setLoading(false);
          return;
        }

        // Fetch scene data
        const sceneData = await parallaxService.loadScene(sceneName);

        if (!sceneData) {
          setError(`Scene "${sceneName}" not found.`);
          setLoading(false);
          return;
        }

        setScene(sceneData);
        setSceneStickers(sceneData.scene_stickers || []);
      } catch (err) {
        console.error('❌ Error loading scene:', err);
        setError('Unable to load scene. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadScene();
  }, []);

  // Handle back to gallery
  const handleBackToGallery = () => {
    sessionStorage.removeItem('viewingSceneName');
    navigationService.navigateTo('gallery');
  };

  // Loading state
  if (loading) {
    return (
      <>
        <MainHeader currentPage="gallery" />
        <div className="scene-viewer loading">
          <div className="loading-message">Loading scene...</div>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <MainHeader currentPage="gallery" />
        <div className="scene-viewer error">
          <div className="error-container">
            <MaterialIcon icon="error" size={64} />
            <h2>Unable to Load Scene</h2>
            <p>{error}</p>
            <button className="back-to-gallery-btn" onClick={handleBackToGallery}>
              <MaterialIcon icon="arrow_back" size={20} />
              Back to Gallery
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <MainHeader currentPage="gallery" />
      <div className="scene-viewer">
        {/* Scene Header */}
        <div className="scene-viewer-header">
          <button className="back-btn" onClick={handleBackToGallery}>
            <MaterialIcon icon="arrow_back" size={20} />
            <span>Back to Gallery</span>
          </button>

          <div className="scene-info">
            <h1>{scene.name}</h1>
            {scene.description && <p className="scene-description">{scene.description}</p>}
          </div>

          <div className="scene-meta">
            {scene.category && (
              <div className="meta-item">
                <MaterialIcon icon="category" size={16} />
                <span>{scene.category}</span>
              </div>
            )}
            <div className="meta-item">
              <MaterialIcon icon="layers" size={16} />
              <span>{sceneStickers.length} stickers</span>
            </div>
          </div>
        </div>

        {/* Static Scene Display */}
        <div className="scene-viewer-display">
          <StaticSceneDisplay
            sceneStickers={sceneStickers}
            height={scene.component_height || '60vh'}
            backgroundColor="#1a1a1a"
          />
        </div>

        {/* Scene Footer */}
        <div className="scene-viewer-footer">
          <button className="back-to-gallery-btn-large" onClick={handleBackToGallery}>
            <MaterialIcon icon="arrow_back" size={20} />
            Back to Gallery
          </button>
        </div>
      </div>
    </>
  );
};

export default SceneViewer;
